/**
 * ImageCropModal
 * -----------------------------------------------------------------------------
 * Luxury admin crop tool for product photos. Powered by react-easy-crop.
 * - Preset aspect ratios: Original / 1:1 / 4:5 / 3:4 / 16:9 / Free
 * - Zoom slider + drag-to-pan (mouse + touch + pinch on mobile)
 * - Preview strip (product-card, product-page, mobile hero) before save
 * - Exports as WebP quality 88 for luxury quality with ~60% smaller file
 * - Never mutates the original — the caller decides how to persist both
 * - Fully keyboard accessible; ESC closes, Enter saves
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { X, RotateCcw, Save, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { key: "original", label: "Original", ratio: null },
  { key: "1:1", label: "1:1", ratio: 1 },
  { key: "4:5", label: "4:5", ratio: 4 / 5 },
  { key: "3:4", label: "3:4", ratio: 3 / 4 },
  { key: "16:9", label: "16:9", ratio: 16 / 9 },
  { key: "free", label: "Free", ratio: undefined /* means unconstrained */ },
];

// Load an image from URL as an HTMLImageElement (with crossOrigin for canvas export).
const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e || new Error("Image failed to load"));
    img.src = url;
  });

// Crop an image on canvas and return a WebP Blob.
const cropToBlob = async (imageUrl, cropPixels, mime = "image/webp", quality = 0.88) => {
  const img = await loadImage(imageUrl);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cropPixels.width);
  canvas.height = Math.round(cropPixels.height);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))),
      mime,
      quality
    );
  });
};

export const ImageCropModal = ({ open, imageUrl, initialAspectKey = "4:5", onCancel, onSave }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectKey, setAspectKey] = useState(initialAspectKey);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [naturalRatio, setNaturalRatio] = useState(4 / 5);
  const previewRef = useRef(null);

  const preset = useMemo(
    () => PRESETS.find((p) => p.key === aspectKey) || PRESETS[2],
    [aspectKey]
  );
  const effectiveAspect = useMemo(() => {
    if (aspectKey === "original") return naturalRatio;
    if (aspectKey === "free") return undefined; // undefined lets user drag freely
    return preset.ratio;
  }, [aspectKey, preset, naturalRatio]);

  // Preload the image so we know the natural aspect for "Original" preset.
  useEffect(() => {
    let cancelled = false;
    setImgLoaded(false);
    if (!open || !imageUrl) return;
    loadImage(imageUrl)
      .then((img) => {
        if (cancelled) return;
        setNaturalRatio(img.naturalWidth / img.naturalHeight);
        setImgLoaded(true);
      })
      .catch(() => setImgLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [imageUrl, open]);

  // Reset transform when re-opening or switching preset.
  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [open, aspectKey]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (!pixels) return toast.error("Adjust the crop first");
    setBusy(true);
    try {
      const blob = await cropToBlob(imageUrl, pixels, "image/webp", 0.88);
      await onSave?.(blob, {
        width: Math.round(pixels.width),
        height: Math.round(pixels.height),
        aspect: aspectKey,
      });
    } catch (e) {
      toast.error(e.message || "Could not export the crop");
    } finally {
      setBusy(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
      if (e.key === "Enter" && !busy) handleSave();
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)));
      if (e.key === "-") setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy, pixels]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="image-crop-modal"
      className="fixed inset-0 z-[95] bg-black/90 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 md:px-8 py-3 border-b border-white/10 shrink-0">
        <div>
          <div className="text-white overline tracking-[3px] text-[11px]">Crop product image</div>
          <div className="text-white/50 text-[11px] mt-1 hidden md:block">Drag to pan · scroll to zoom · pinch on touch · Esc to cancel · Enter to save</div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close crop"
          data-testid="crop-close"
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body — desktop: cropper (left) + preview column (right); mobile: stacked */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        {/* Cropper stage */}
        <div className="relative flex-1 min-h-[52vh] lg:min-h-0 bg-black" data-testid="crop-stage">
          {imageUrl && imgLoaded ? (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={effectiveAspect}
              cropShape="rect"
              showGrid
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              zoomWithScroll
              restrictPosition
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/50 text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading image…
            </div>
          )}
        </div>

        {/* Side panel — presets, zoom, preview, save */}
        <aside className="w-full lg:w-[320px] shrink-0 bg-[#0b0b0b] text-white border-t lg:border-t-0 lg:border-l border-white/10 overflow-y-auto">
          <div className="p-4 md:p-5 space-y-5">
            {/* Aspect presets */}
            <div>
              <div className="overline text-white/60 text-[10px] tracking-[2px] mb-2">Aspect ratio</div>
              <div className="grid grid-cols-3 gap-2" data-testid="crop-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setAspectKey(p.key)}
                    data-testid={`crop-preset-${p.key}`}
                    className={`overline text-[10px] tracking-[2px] py-2 rounded border transition-colors ${
                      aspectKey === p.key
                        ? "border-gold bg-gold/10 text-gold"
                        : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Zoom */}
            <div>
              <div className="overline text-white/60 text-[10px] tracking-[2px] mb-2 flex items-center justify-between">
                <span>Zoom</span>
                <span className="tabnum text-white/80 normal-case">{zoom.toFixed(1)}×</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)))}
                  aria-label="Zoom out"
                  data-testid="crop-zoom-out"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                >
                  <ZoomOut size={14} />
                </button>
                <input
                  type="range"
                  min={1}
                  max={4}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  data-testid="crop-zoom-range"
                  className="flex-1 accent-gold"
                />
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))}
                  aria-label="Zoom in"
                  data-testid="crop-zoom-in"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                >
                  <ZoomIn size={14} />
                </button>
              </div>
            </div>

            {/* Preview strip */}
            <div>
              <div className="overline text-white/60 text-[10px] tracking-[2px] mb-2">Preview</div>
              <div className="grid grid-cols-3 gap-3" data-testid="crop-previews" ref={previewRef}>
                <PreviewTile label="Card" url={imageUrl} pixels={pixels} boxClass="aspect-[4/5]" />
                <PreviewTile label="PDP" url={imageUrl} pixels={pixels} boxClass="aspect-[4/5]" />
                <PreviewTile label="Mobile" url={imageUrl} pixels={pixels} boxClass="aspect-[3/4]" />
              </div>
              <div className="text-white/40 text-[10px] mt-2">Live crop preview at storefront ratios.</div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); }}
                data-testid="crop-reset"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 hover:text-white hover:border-white/50 overline text-[10px] tracking-[2px] py-2.5 rounded"
              >
                <RotateCcw size={12} /> Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy || !pixels}
                data-testid="crop-save"
                className="inline-flex items-center justify-center gap-2 bg-gold text-black hover:bg-champagne overline text-[10px] tracking-[2px] py-2.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save crop
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

/**
 * PreviewTile — renders a canvas-free CSS preview of the cropped region.
 * Uses background-image + background-position/size math so it's instant and
 * doesn't need a re-export for every drag.
 */
const PreviewTile = ({ label, url, pixels, boxClass }) => {
  const style = useMemo(() => {
    if (!url || !pixels) return { backgroundColor: "#1a1a1a" };
    // Load natural dims from cache via a synchronous <img> is tricky in React —
    // use pixels.width / pixels.height directly for the sizing viewport instead.
    return {
      backgroundImage: `url(${url})`,
      backgroundRepeat: "no-repeat",
      backgroundColor: "#1a1a1a",
      // Position the image so only the cropped rectangle is visible.
      // We use background-size = 100% relative to the crop box.
      backgroundSize: `${(1 / (pixels.width || 1)) * 100}%,`, // placeholder — overridden by BG below
    };
  }, [url, pixels]);

  return (
    <div className={`relative overflow-hidden rounded border border-white/10 ${boxClass} bg-neutral-900`}>
      {url && pixels && <CroppedCanvasPreview url={url} pixels={pixels} />}
      <div className="absolute bottom-1 left-1 text-white/70 text-[9px] uppercase tracking-widest bg-black/60 px-1.5 py-0.5 rounded">
        {label}
      </div>
    </div>
  );
};

/**
 * Draws the cropped rectangle onto a hidden canvas and shows it as a <img>.
 * Cheap — only rebuilds when pixels change.
 */
const CroppedCanvasPreview = ({ url, pixels }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const img = await loadImage(url);
        const canvas = document.createElement("canvas");
        // Downsize preview: max 240px on the long side keeps re-renders cheap.
        const maxSide = 240;
        const scale = Math.min(1, maxSide / Math.max(pixels.width, pixels.height));
        canvas.width = Math.max(1, Math.round(pixels.width * scale));
        canvas.height = Math.max(1, Math.round(pixels.height * scale));
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "medium";
        ctx.drawImage(img, pixels.x, pixels.y, pixels.width, pixels.height, 0, 0, canvas.width, canvas.height);
        const url2 = canvas.toDataURL("image/webp", 0.7);
        if (!cancelled) setSrc(url2);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [url, pixels]);
  if (!src) return null;
  return <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />;
};

export default ImageCropModal;
